export type GameSnapshotKind = 'hook' | 'data' | 'full'
export const MAX_TIMER_NETWORK_AGE_SECONDS = 10

export interface GameSnapshotIdentity {
  id: string
  sessionId?: string
  revision?: number
  round?: number
  mode?: number
  terminal?: boolean
}

export interface SnapshotDecision {
  apply: boolean
  timerOnly: boolean
  reason:
    | 'accepted'
    | 'different-game'
    | 'different-session'
    | 'stale-revision'
    | 'future-hook'
    | 'different-round'
    | 'stale-round'
    | 'revision-conflict'
    | 'terminal-regression'
}

export function shouldSendGameLeave(
  currentRoomId: string,
  nextRouteName?: string | null,
  nextRoomId?: string
): boolean {
  return nextRouteName !== 'room' || nextRoomId !== currentRoomId
}

export function shouldOpenStartedGame(
  roomIsPlaying: boolean,
  gameLoaded: boolean,
  gameHasPlayer: boolean
): boolean {
  return roomIsPlaying && gameLoaded && gameHasPlayer
}

export function shouldRedirectMissingRoomPlayer(
  hasPlayer: boolean
): boolean {
  return !hasPlayer
}

export function shouldApplyRoomSnapshot(
  currentId: string,
  currentRevision: number | undefined,
  incomingId: string,
  incomingRevision: number | undefined
): boolean {
  if (currentId !== incomingId) return false
  return (incomingRevision ?? -1) >= (currentRevision ?? -1)
}

export function decideGameSnapshot(
  current: GameSnapshotIdentity,
  incoming: GameSnapshotIdentity,
  kind: GameSnapshotKind
): SnapshotDecision {
  const currentRevision = current.revision ?? -1
  const incomingRevision = incoming.revision ?? -1
  const comparableRevision = current.revision !== undefined && currentRevision >= 0 && incoming.revision !== undefined

  if (current.id && incoming.id && current.id !== incoming.id) {
    return { apply: false, timerOnly: false, reason: 'different-game' }
  }

  if (current.sessionId && current.sessionId !== incoming.sessionId) {
    return { apply: false, timerOnly: false, reason: 'different-session' }
  }

  if (
    current.sessionId && current.sessionId === incoming.sessionId &&
    current.terminal === true && incoming.terminal === false
  ) {
    return { apply: false, timerOnly: false, reason: 'terminal-regression' }
  }

  if (incomingRevision < currentRevision) {
    return { apply: false, timerOnly: false, reason: 'stale-revision' }
  }

  if (
    current.round !== undefined && incoming.round !== undefined &&
    incoming.round < current.round
  ) {
    return { apply: false, timerOnly: false, reason: 'stale-round' }
  }

  if (
    comparableRevision && incomingRevision === currentRevision &&
    current.round !== undefined && incoming.round !== undefined &&
    incoming.round !== current.round
  ) {
    return { apply: false, timerOnly: false, reason: 'different-round' }
  }

  if (
    kind !== 'hook' && comparableRevision && incomingRevision === currentRevision &&
    current.mode !== undefined && incoming.mode !== undefined &&
    incoming.mode !== current.mode
  ) {
    return { apply: false, timerOnly: false, reason: 'revision-conflict' }
  }

  if (kind === 'hook') {
    if (incomingRevision !== currentRevision) {
      return { apply: false, timerOnly: false, reason: 'future-hook' }
    }
    return { apply: true, timerOnly: true, reason: 'accepted' }
  }

  return { apply: true, timerOnly: false, reason: 'accepted' }
}

export function ageTimerValue(incomingTimer: number, generatedAt?: number, now = Date.now()): number {
  const safeIncoming = Math.max(0, Number(incomingTimer) || 0)
  if (!generatedAt || !Number.isFinite(generatedAt)) return safeIncoming

  const elapsedSeconds = Math.min(
    MAX_TIMER_NETWORK_AGE_SECONDS,
    Math.floor(Math.max(0, now - generatedAt) / 1000)
  )
  return Math.max(0, safeIncoming - elapsedSeconds)
}

export function nextTimerValue(
  currentTimer: number,
  incomingTimer: number,
  samePhase: boolean,
  generatedAt?: number,
  now = Date.now()
): number {
  const agedIncoming = ageTimerValue(incomingTimer, generatedAt, now)
  if (!samePhase) return agedIncoming
  return Math.min(Math.max(0, Number(currentTimer) || 0), agedIncoming)
}
